/**
 * Application Session Service - Key-value storage in Dexie
 * Replaces browser localStorage with IndexedDB storage
 */

import db from '@/db';
import { ApplicationSession } from '../types/application_session';

import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('ApplicationSession', LogLevel.DEBUG);
/**
 * Get an item value by key
 * @param key The item key
 * @returns Promise with the item value or null if not found
 */
export async function getItem<T>(key: string): Promise<T | null> {
  try {
    const item = await db.applicationSession.get(key);
    return item ? (item.value as T) : null;
  } catch (error) {
    logger.error(`Failed to get item "${key}":`, error);
    return null;
  }
}

/**
 * Set an item value
 * @param key The item key
 * @param value The value to store (must be JSON-serializable)
 */
export async function setItem<T>(key: string, value: T): Promise<void> {
  try {
    const item: ApplicationSession = {
      key,
      value,
      updated_at: new Date().toISOString()
    };
    await db.applicationSession.put(item);
  } catch (error) {
    logger.error(`Failed to set item "${key}":`, error);
  }
}

/**
 * Remove an item
 * @param key The item key to remove
 */
export async function removeItem(key: string): Promise<void> {
  try {
    await db.applicationSession.delete(key);
  } catch (error) {
    logger.error(`Failed to remove item "${key}":`, error);
  }
}

/**
 * Check if an item exists
 * @param key The item key
 * @returns Promise with true if the item exists
 */
export async function hasItem(key: string): Promise<boolean> {
  try {
    const item = await db.applicationSession.get(key);
    return item !== undefined;
  } catch (error) {
    logger.error(`Failed to check item "${key}":`, error);
    return false;
  }
}
